/**
 * siteContentRules.ts
 * Rules section defaults (large file, split out for maintainability).
 */
import type { RuleSection, RulesCTAData } from "./siteContentTypes";

export const defaultRules: RuleSection[] = [
  {
    id: "product-guidelines",
    icon: "ri-shield-star-line",
    iconColor: "text-pink-500",
    iconBg: "bg-pink-50",
    title: "Product Guidelines",
    subsections: [
      {
        heading: "The following categories are not allowed:",
        body: "",
        list: [
          "Stolen, copied, traced, or infringing artwork",
          "AI-generated artwork",
          "Political content, extreme violence/gore, pornography, or erotic content",
          "Mass-produced resale items (Temu, AliExpress, etc.)",
        ],
        listStyle: "warning",
      },
      {
        heading: "Copyright & Fan Work",
        body: "Fan-inspired work is welcome, but you are fully responsible for making sure your products follow U.S. copyright and IP laws. Makers Alley Co. does not check licensing or permissions.\n\nIf an IP owner contacts us, we may remove the affected items immediately.\n\nTransformative works may be accepted if they show clear originality.\n\nIf you are uncertain whether a product meets our guidelines, please contact us before placing it in your space.\n\nAdding items that violate these rules may result in a warning and removal of the non-compliant products.",
      },
      {
        heading: "Creative Ownership",
        body: "Your products must be designed or created by you. Manufacturing can happen anywhere, but the creative work must be yours.\n\nMakers Alley Co. reserves the right to decline products that don't fit our shop's standards, aesthetic, or community guidelines.",
      },
    ],
  },
  {
    id: "stock-requirements",
    icon: "ri-store-2-line",
    iconColor: "text-green-600",
    iconBg: "bg-green-50",
    title: "Stock Requirements",
    subsections: [
      {
        body: "Shelves must remain consistently stocked. Each design should have at least 3 units available unless it's oversized, high-value, or one-of-a-kind.\n\nRepeated failure to maintain stock may result in a warning or, in serious cases, membership termination.\n\nVendors are welcome to restock anytime during open hours except the final hour before closing.",
      },
    ],
  },
  {
    id: "overstock",
    icon: "ri-archive-line",
    iconColor: "text-pink-500",
    iconBg: "bg-pink-50",
    title: "Restocking & Overstock Storage",
    subsections: [
      {
        body: "You may keep extra inventory in your assigned overstock box so our team can refill your shelf as items sell. Overstock is optional but highly recommended to avoid empty shelves.",
        heading: "Overstock Storage",
      },
      {
        heading: "Approved box sizes (U.S. inches):",
        body: "",
        list: [
          "Showcase Shelf & Entry Shelf: up to 16\" x 12\" x 6\"",
          "Full Shelf, Wall Display, Explicit Wall: up to 16\" x 12\" x 12\"",
          "Large Wall Display: one 24\" x 16\" x 12\" box or two 16\" x 12\" x 12\" boxes",
        ],
        listStyle: "bullet",
      },
      {
        heading: "Your overstock box must:",
        body: "",
        list: [
          "Have a lid",
          "Be clear/transparent",
          "Be labeled with your artist name on all sides",
          "Contain items already labeled for sale",
        ],
        listStyle: "bullet",
      },
    ],
  },
  {
    id: "shelf-types",
    icon: "ri-layout-grid-line",
    iconColor: "text-green-600",
    iconBg: "bg-green-50",
    title: "Shelf Types & Dimensions",
    subsections: [
      {
        body: "Below is an overview of each shelf option, including term length, what's provided, and the matching overstock bin size. All measurements are in U.S. inches.",
      },
      {
        heading: "SHOWCASE SHELF CANCELLATION",
        body: "Term: 1-month maximum (reapply quarterly)\nShelf: Provided\nDisplay: Provided by artist.\nShelf Size: 13.9\" W \u00d7 12\" D \u00d7 14.5\" H\nOverstock Bin Size: Up to 16\" \u00d7 12\" \u00d7 6\"\n\nRefund Policy:\n\u2022 Cancellations made at least one month before the cycle begins: full refund (minus processing fees)\n\u2022 Cancellations made less than one month before the cycle begins: non-refundable\n\nVendors may request to move their reservation to a later Showcase cycle if the request is made at least one month before the assigned start date. Requests made after that point cannot be accommodated, and missed cycles are non-refundable.",
      },
      {
        heading: "SHOWCASE SHELF",
        body: "Term: 3-month minimum\nShelf: Provided\nDisplay: Artist provides their own display pieces\nShelf Size: 13.9\" W \u00d7 12\" D \u00d7 14.5\" H (4 tiers)\nOverstock Bin Size: Up to 16\" \u00d7 12\" \u00d7 6.25\"",
      },
      {
        heading: "STANDARD SHELF",
        body: "Term: 3-month minimum\nShelf: Provided\nDisplay: Artist provides their own display pieces\nShelf Size: 27.95\" W \u00d7 12\" D \u00d7 14.5\" H (4 tiers)\nOverstock Bin Size: Up to 14\" \u00d7 10\" \u00d7 10.75\"",
      },
      {
        heading: "PREMIUM SHELF",
        body: "Term: 3-month minimum\nShelf/Display: Artist provides their own setup\nMax Display Size: 2 ft W \u00d7 1.5 ft D \u00d7 7 ft H\nOverstock Bin Size: Up to 14\" \u00d7 10\" \u00d7 10.75\"",
      },
      {
        heading: "STANDARD WALL DISPLAY",
        body: "Term: 3-month minimum\nShelf/Display: Artist provides their own setup\nMax Display Size: 3 ft W \u00d7 1.5 ft D \u00d7 7 ft H\nOverstock Bin Options: One 24\" \u00d7 16\" \u00d7 12\" bin, or two 16\" \u00d7 12\" \u00d7 12\" bins",
      },
      {
        heading: "PREMIUM WALL DISPLAY",
        body: "Term: 3-month minimum\nShelf/Display: Artist provides their own setup\nMax Display Size: 4 ft W \u00d7 1.5 ft D \u00d7 7 ft H\nOverstock Bin Options: One 24\" \u00d7 16\" \u00d7 12\" bin, or two 16\" \u00d7 12\" \u00d7 12\" bins",
      },
    ],
  },
  {
    id: "placement",
    icon: "ri-map-pin-2-line",
    iconColor: "text-pink-500",
    iconBg: "bg-pink-50",
    title: "Placement & Display Adjustments",
    subsections: [
      {
        body: "We assign shelf placement to keep the shop visually balanced. Placement requests cannot be accommodated.\n\nWe may adjust displays to improve customer flow. Minor adjustments may be made without notice. Major changes will always be communicated.\n\nOut-of-town artists may request display modifications. Setup fees may apply.",
      },
      {
        heading: "Out-of-Town Vendors",
        body: "If you can't set up your display in person, you may ship your products and display materials to us. A setup fee applies depending on the shelf type.",
        list: [
          "Showcase & Standard Shelf: $50",
          "Premium Shelf, Standard Wall Display: $80",
          "Premium Wall Display: $100",
        ],
        listStyle: "bullet",
      },
      {
        body: "Future display changes may be complimentary, or incur additional fees depending on the work required. Any fees will be communicated in advance.",
      },
    ],
  },
  {
    id: "labeling",
    icon: "ri-price-tag-3-line",
    iconColor: "text-green-600",
    iconBg: "bg-green-50",
    title: "Labeling & Printing Services",
    subsections: [
      {
        heading: "Labeling Service",
        body: "If you cannot label your products before drop-off, we can label them for you:",
        list: [
          "First 5 labels: Complimentary",
          "6\u201350 labels: $25",
          "50\u2013100 labels: $50",
          "100\u2013150 labels: $75",
          "150\u2013200 labels: $100",
        ],
        listStyle: "bullet",
      },
      {
        heading: "Printing Service",
        body: "$2 per page. Files must be submitted print-ready in PDF format at the correct size.",
      },
    ],
  },
  {
    id: "deposit",
    icon: "ri-secure-payment-line",
    iconColor: "text-pink-500",
    iconBg: "bg-pink-50",
    title: "Deposit",
    subsections: [
      {
        body: "All vendors (except Showcase Shelf) pay their final month's subscription upfront. This serves as both your prepaid last month and a security deposit if a vendor leaves without notice or fails to pay.",
      },
    ],
  },
  {
    id: "application-process",
    icon: "ri-file-list-3-line",
    iconColor: "text-green-600",
    iconBg: "bg-green-50",
    title: "Application Process & Timeline",
    subsections: [
      {
        body: "After you apply, expect a response within 1 month. We utilize a jury-based selection process.",
        heading: "We consider:",
        list: [
          "Creativity",
          "Craftsmanship",
          "Product variety",
          "Presentation",
          "Online presence",
        ],
        listStyle: "bullet",
      },
      {
        body: "We welcome emerging artists! Strength in even one area can be enough.",
        heading: "Once accepted:",
        list: [
          "Pay your deposit within 7 days to secure your spot",
          "Choose a start date within 30 days",
          "Your display must be fully set up within 15 days of your start date",
          "Activate your subscription within 3 days of receiving the link",
        ],
        listStyle: "bullet",
      },
    ],
  },
  {
    id: "eligibility",
    icon: "ri-user-star-line",
    iconColor: "text-pink-500",
    iconBg: "bg-pink-50",
    title: "Eligibility",
    subsections: [
      {
        body: "To join Makers Alley Co., you'll need to be an independent creator or business selling your own work. Renting space here doesn't make you an employee \u2014 you run your own business, and we simply provide the place for you to showcase and sell your products.\n\nYou can participate as a sole proprietor, registered business, or creative brand. To receive payouts, you'll need a U.S.-based bank account or payment method.\n\nVendors are responsible for following all applicable federal and state laws, including taxes and business registration. Makers Alley Co. doesn't take ownership of your products; we display them, promote them, and process sales on your behalf while you keep full creative and business control.\n\nWe do not review or verify your business licensing or tax status \u2014 that part is entirely up to you.",
      },
    ],
  },
  {
    id: "insurance",
    icon: "ri-shield-check-line",
    iconColor: "text-green-600",
    iconBg: "bg-green-50",
    title: "Insurance & Liability",
    subsections: [
      {
        body: "We strongly recommend carrying business or property insurance to protect your work from unexpected events.\n\nWhile we take extensive measures to maintain a secure environment, Makers Alley Co. cannot assume liability for incidents outside our control \u2014 including but not limited to theft, vandalism, or natural disasters. Our space is monitored continuously with security cameras, and interior barriers are in place to deter unauthorized access. Security footage may be made available if needed for incident review.",
      },
    ],
  },
  {
    id: "payouts",
    icon: "ri-money-dollar-circle-line",
    iconColor: "text-pink-500",
    iconBg: "bg-pink-50",
    title: "Payouts",
    subsections: [
      {
        body: "Payouts are sent on the 1st of each month, or the next business day if we're closed or it's a holiday.\n\nVendors receive 100% of their sales revenue.\n\nCreators are responsible for their own taxes. Makers Alley Co. does not issue tax forms or file taxes for you.\n\nWe act only as a display and sales venue \u2014 not a reseller. Sales are pass-through transactions:",
        list: [
          "We do not collect or remit sales tax for your products",
          "If you need to charge sales tax, include it in your retail price",
          "You are responsible for staying compliant with federal and Oregon tax laws",
        ],
        listStyle: "bullet",
      },
    ],
  },
  {
    id: "billing",
    icon: "ri-calendar-line",
    iconColor: "text-green-600",
    iconBg: "bg-green-50",
    title: "Subscription Billing",
    subsections: [
      {
        body: "Shelf subscriptions begin on the day your display goes live and renew automatically on that same date each month.",
        heading: "If a payment is late:",
        list: [
          "Your payout will be held until the payment is completed",
          "A late notice will be sent after 3 days",
        ],
        listStyle: "bullet",
      },
      {
        heading: "Your membership may be ended immediately if:",
        body: "",
        list: [
          "A payment becomes 15 days overdue, or",
          "You receive three late notices within six months",
        ],
        listStyle: "warning",
      },
      {
        body: "If terminated, your products will be removed and your prepaid last-month deposit will be applied to the current month's subscription.\n\nSubscription rates may change in the future, but any update will only apply to upcoming billing cycles.",
      },
    ],
  },
  {
    id: "moveout",
    icon: "ri-door-open-line",
    iconColor: "text-pink-500",
    iconBg: "bg-pink-50",
    title: "Move Out Process",
    subsections: [
      {
        body: "After your cancellation is confirmed, our staff will contact you to schedule your move-out.\n\nMove-outs must occur on weekdays, and vendors must arrive before 1 PM.",
        heading: "On your move-out day:",
        list: [
          "Remove all products and, if applicable, your shelf",
          "Leave your space clean and free of debris",
          "Patch any wall holes created by drilling",
        ],
        listStyle: "bullet",
      },
      {
        body: "Your final payout will be issued on the 1st of the following month, or the next business day if we're closed or it's a holiday.\n\nOut-of-town vendors may request return shipping for shelves or products; shipping costs are the vendor's responsibility.",
      },
    ],
  },
  {
    id: "return-policy",
    icon: "ri-arrow-go-back-line",
    iconColor: "text-pink-500",
    iconBg: "bg-pink-50",
    title: "Return Policy",
    subsections: [
      {
        body: "Changed your mind? You have 14 days to exchange or return a product to the same location it was purchased from. The product must be in its original condition, both unused and unworn to qualify for a return or exchange. If you have any additional questions please reach out to the store in which you made your purchase. Thank you!",
      },
    ],
  },
  {
    id: "enforcement",
    icon: "ri-alert-line",
    iconColor: "text-green-600",
    iconBg: "bg-green-50",
    title: "Rule Enforcement",
    subsections: [
      {
        heading: "Warning Notices",
        body: "Policy violations may result in a written Warning Notice. The decision to issue a warning is made on a case-by-case basis.",
      },
      {
        heading: "Three Warning Notices",
        body: "If a vendor receives three Warning Notices, their rental agreement may be terminated. The vendor's last-month deposit will be applied toward their final rental payment, and a move-out date will be scheduled. Final payouts will be issued after the vendor has fully vacated their space.",
      },
      {
        heading: "Immediate Termination",
        body: "If a situation escalates into a significant dispute or breach of contract, Makers Alley Co. may terminate the agreement immediately. In such cases, the final payout and last-month deposit may be withheld to cover any outstanding rent, damages, or costs resulting from the breach.",
      },
    ],
  },
];

export const defaultRulesCTA: RulesCTAData = {
  heading: "Ready to join our community?",
  subtext: "Review all policies above, then submit your application.",
  primaryLabel: "Apply Now",
  primaryHref: "#application",
  secondaryLabel: "Ask a Question",
  secondaryHref: "#contact",
  sectionEyebrow: "Community Standards",
  sectionHeading: "Rules & Guidelines",
  sectionSubtext: "Our retail space thrives on quality, originality, and respect. Please read all policies carefully before applying.",
};